import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, MessageSquarePlus, Search, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import { RiqueIcon } from '@/components/Rique/RiqueIcon';
import type { RiqueConversation } from '@/lib/riqueApi';

interface RiqueConversationSidebarProps {
  conversations: RiqueConversation[];
  activeId: string | null;
  onSelect: (id: string) => void;
  onNew: () => void;
  onDelete?: (id: string) => Promise<void>;
  onDeleteAll?: () => Promise<void>;
  onOpenFaq?: () => void;
  faqTotal?: number;
  className?: string;
}

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

function conversationGroupLabel(iso: string | undefined, now = new Date()): string {
  if (!iso) return 'Anteriores';
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return 'Anteriores';
  const diffDays = Math.round((startOfDay(now) - startOfDay(t)) / 86_400_000);
  if (diffDays <= 0) return 'Hoje';
  if (diffDays === 1) return 'Ontem';
  if (diffDays < 7) return 'Últimos 7 dias';
  return 'Anteriores';
}

export const RiqueConversationSidebar: React.FC<RiqueConversationSidebarProps> = ({
  conversations,
  activeId,
  onSelect,
  onNew,
  onDelete,
  onDeleteAll,
  onOpenFaq,
  faqTotal,
  className,
}) => {
  const [deletingId, setDeletingId] = React.useState<string | null>(null);
  const [clearingAll, setClearingAll] = React.useState(false);
  const [query, setQuery] = React.useState('');

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return conversations;
    return conversations.filter(
      (c) =>
        c.title.toLowerCase().includes(q) ||
        (c.company?.name ?? '').toLowerCase().includes(q)
    );
  }, [conversations, query]);

  const grouped = React.useMemo(() => {
    const map = new Map<string, RiqueConversation[]>();
    const order = ['Hoje', 'Ontem', 'Últimos 7 dias', 'Anteriores'];
    for (const c of filtered) {
      const label = conversationGroupLabel(c.updatedAt ?? c.createdAt);
      const list = map.get(label) ?? [];
      list.push(c);
      map.set(label, list);
    }
    return order
      .filter((label) => (map.get(label)?.length ?? 0) > 0)
      .map((label) => ({ label, items: map.get(label)! }));
  }, [filtered]);

  const handleDelete = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (!onDelete) return;
    if (!window.confirm('Excluir esta conversa? Esta ação não pode ser desfeita.')) return;
    setDeletingId(id);
    try {
      await onDelete(id);
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteAll = async () => {
    if (!onDeleteAll || conversations.length === 0) return;
    if (!window.confirm('Excluir todo o histórico de conversas? Esta ação não pode ser desfeita.')) return;
    setClearingAll(true);
    try {
      await onDeleteAll();
    } finally {
      setClearingAll(false);
    }
  };

  return (
    <aside
      className={cn(
        'flex h-full min-h-0 w-full flex-col border-r border-border/70 bg-muted/30',
        className
      )}
      aria-label="Conversas Rique.Ia"
    >
      <div className="flex shrink-0 items-center gap-2 border-b border-border/60 px-3 py-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white p-0.5 shadow-sm ring-1 ring-border/40">
          <RiqueIcon className="h-full w-full" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-foreground">Rique.Ia</p>
          <p className="truncate text-[10px] text-muted-foreground">Assistente analista</p>
        </div>
      </div>

      <div className="shrink-0 space-y-2 p-3">
        <Button type="button" className="w-full justify-start gap-2" onClick={onNew}>
          <MessageSquarePlus className="h-4 w-4" aria-hidden />
          Nova conversa
        </Button>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar conversas…"
            className="h-9 w-full rounded-xl border border-input bg-background pl-8 pr-2 text-xs text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label="Buscar conversas"
          />
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">
        {grouped.length === 0 ? (
          <p className="px-2 py-6 text-center text-xs text-muted-foreground">
            {conversations.length === 0 ? 'Nenhuma conversa ainda.' : 'Nenhum resultado.'}
          </p>
        ) : (
          grouped.map((group) => (
            <div key={group.label} className="mb-3">
              <p className="px-2.5 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                {group.label}
              </p>
              {group.items.map((c) => (
                <div
                  key={c.id}
                  className={cn(
                    'group mb-0.5 flex items-stretch gap-0.5 rounded-xl transition-colors',
                    activeId === c.id ? 'bg-background shadow-sm ring-1 ring-border/50' : 'hover:bg-background/70'
                  )}
                >
                  <button
                    type="button"
                    onClick={() => onSelect(c.id)}
                    className={cn(
                      'min-w-0 flex-1 rounded-xl px-2.5 py-2 text-left text-xs',
                      activeId === c.id ? 'text-foreground' : 'text-muted-foreground'
                    )}
                  >
                    <span className="line-clamp-2 font-medium leading-snug text-foreground">{c.title}</span>
                    {c.company?.name ? (
                      <span className="mt-0.5 block truncate text-[10px] opacity-70">{c.company.name}</span>
                    ) : null}
                  </button>
                  {onDelete ? (
                    <button
                      type="button"
                      onClick={(e) => void handleDelete(e, c.id)}
                      disabled={deletingId === c.id || clearingAll}
                      className={cn(
                        'shrink-0 rounded-xl px-2 text-muted-foreground opacity-0 transition-opacity',
                        'hover:bg-destructive/10 hover:text-destructive group-hover:opacity-100 focus-visible:opacity-100',
                        deletingId === c.id && 'opacity-100'
                      )}
                      aria-label={`Excluir conversa ${c.title}`}
                      title="Excluir conversa"
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden />
                    </button>
                  ) : null}
                </div>
              ))}
            </div>
          ))
        )}
      </div>

      <div className="shrink-0 space-y-0.5 border-t border-border/60 p-2">
        <Link
          to="/portal"
          className="flex w-full items-center gap-2 rounded-xl px-2.5 py-2 text-left text-xs text-muted-foreground transition-colors hover:bg-background hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
          Voltar ao portal
        </Link>
        {onOpenFaq ? (
          <button
            type="button"
            onClick={onOpenFaq}
            className="w-full rounded-xl px-2.5 py-2 text-left text-xs text-muted-foreground transition-colors hover:bg-background hover:text-foreground"
          >
            Perguntas frequentes{typeof faqTotal === 'number' ? ` (${faqTotal})` : ''}
          </button>
        ) : null}
        {onDeleteAll && conversations.length > 0 ? (
          <button
            type="button"
            onClick={() => void handleDeleteAll()}
            disabled={clearingAll}
            className="w-full rounded-xl px-2.5 py-2 text-left text-xs text-destructive/80 transition-colors hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
          >
            Limpar histórico
          </button>
        ) : null}
      </div>
    </aside>
  );
};
